import { useEffect } from 'react'
import { DesktopAccountCleanupError } from '@/lib/auth/desktop-account-cleanup-operation'
import { isTauri } from '@/lib/utils/tauri'
import { toast } from 'sonner'

/** Native receipts resume local cleanup only; fresh login stays blocked natively. */
export function DesktopAccountCleanupRecovery() {
  useEffect(() => {
    if (!isTauri()) return
    let mounted = true
    let running = false
    let requested = false
    let unlisten: (() => void) | undefined
    const recover = async () => {
      requested = true
      if (running) return
      running = true
      try {
        while (mounted && requested) {
          requested = false
          try {
            const { resumeDesktopAccountCleanup } =
              await import('@/lib/auth/desktop-account-cleanup')
            const result = await resumeDesktopAccountCleanup()
            if (!mounted) return
            toast.dismiss('desktop-account-cleanup')
            if (result.unattributed)
              toast.warning(
                'Saved sign-in removed. Older local data could not be attributed safely.',
                { id: 'desktop-account-legacy-cleanup' },
              )
          } catch (error) {
            if (
              mounted &&
              error instanceof DesktopAccountCleanupError &&
              error.unattributed
            )
              toast.warning(
                'Saved sign-in removed. Older local data could not be attributed safely.',
                { id: 'desktop-account-legacy-cleanup' },
              )
            if (mounted)
              toast.error(
                'Local account cleanup needs attention before a new sign-in.',
                {
                  id: 'desktop-account-cleanup',
                  duration: Infinity,
                  action: {
                    label: 'Retry cleanup',
                    onClick: () => void recover(),
                  },
                },
              )
          }
        }
      } finally {
        running = false
      }
    }
    void import('@tauri-apps/api/event')
      .then(async ({ listen }) => {
        const stop = await listen(
          'desktop-account-removals-changed',
          () => void recover(),
        )
        if (!mounted) stop()
        else {
          unlisten = stop
          void recover()
        }
      })
      .catch(() => {
        if (mounted) void recover()
      })
    return () => {
      mounted = false
      unlisten?.()
    }
  }, [])
  return null
}
